import { Switch, Route, useLocation } from "wouter";
import { queryClient } from "./lib/queryClient.js";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "./components/ui/toaster.js";
import NotFound from "./pages/not-found.js";
import Home from "./pages/home.js";
import Catalog from "./pages/catalog.js";
import License from "./pages/license.js";
import About from "./pages/about.js";
import Pricing from "./pages/pricing.js";
import Privacy from "./pages/privacy.js";
import Terms from "./pages/terms.js";
import LicensingGuide from "./pages/licensing-guide.js";
import Referral from "./pages/referral.js";
import AdminDashboard from "./pages/admin/dashboard.js";
import AdminRequests from "./pages/admin/requests.js";
import MoodEditor from "./pages/mood-editor.js";
import Header from "./components/layout/header.js";
import Footer from "./components/layout/footer.js";
import MusicPlayer from "./components/layout/music-player.js";
import { useEffect } from "react";
import { useToast } from "./hooks/use-toast.js";
import { MusicPlayerProvider } from "./hooks/use-music-player.js";
import FAQ from "./pages/faq.js";

// Scroll restoration component
function ScrollRestoration() {
  const [location] = useLocation();

  useEffect(() => {
    // Add a small delay to let the new content render
    const timer = setTimeout(() => {
      const mainElement = document.querySelector('main.overflow-y-auto');
      if (mainElement) {
        mainElement.scrollTo({ top: 0, behavior: 'auto' });
      } else {
        window.scrollTo({ top: 0, behavior: 'auto' });
      }
    }, 100); // 100ms delay—adjust if needed

    // Cleanup timeout to prevent memory leaks
    return () => clearTimeout(timer);
  }, [location]);

  return null;
}

function Router() {
  return (
    <div className="flex flex-col min-h-screen bg-background">
      <ScrollRestoration />
      <Header />
      <main className="flex-1 flex flex-col overflow-y-auto">
        <Switch>
          <Route path="/" component={Home} />
          <Route path="/catalog" component={Catalog} />
          <Route path="/license" component={License} />
          <Route path="/pricing" component={Pricing} />
          <Route path="/about" component={About} />
          <Route path="/privacy" component={Privacy} />
          <Route path="/terms" component={Terms} />
          <Route path="/licensing-guide" component={LicensingGuide} />
          <Route path="/faq" component={FAQ} />
          <Route path="/referral" component={Referral} />
          <Route path="/mood-editor" component={MoodEditor} />
          <Route path="/admin" component={AdminDashboard} />
          <Route path="/admin/requests" component={AdminRequests} />
          <Route component={NotFound} />
        </Switch>
        <MusicPlayer />
      </main>
      <Footer />
    </div>
  );
}

function App() {
  const { toast } = useToast();

  useEffect(() => {
    // Check for necessary API keys using type-safe approach for Vite env vars
    // @ts-ignore - Suppress env type error, it's properly defined in vite-env.d.ts
    const apiKey = import.meta.env.VITE_STRIPE_PUBLIC_KEY;
    if (!apiKey) {
      toast({
        title: "Configuration Warning",
        description: "Stripe API key is missing. Payment functionality will be limited.",
        variant: "destructive",
      });
    }
  }, [toast]);

  // Apply CSS to prevent over-scrolling
  useEffect(() => {
    document.documentElement.style.overscrollBehavior = 'none';
    document.body.style.overscrollBehavior = 'none';
    
    return () => {
      document.documentElement.style.overscrollBehavior = '';
      document.body.style.overscrollBehavior = '';
    };
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <MusicPlayerProvider>
        <Router />
        <Toaster />
      </MusicPlayerProvider>
    </QueryClientProvider>
  );
}

export default App;
